import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { AI_SIMULATION_TYPES } from 'common/constants/AISimulationConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { PERMISSION_DENIED_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { formatSelectValue } from 'common/utils/utilFunctions';

import {
	IFourParamFn,
	IGenerateParamFn,
	IRegulationParamFn,
	InputOutputHookProp,
} from '../api/type-def';

export default function useInputOutput({
	aiModelKey,
	workflowStep,
	requestFn,
	reqPromptSelect,
}: InputOutputHookProp) {
	const [inputTxt, setInputTxt] = useState('');
	const [outputResult, setOutputResult] = useState('');
	const [isGenerating, setIsGenerating] = useState(false);

	const dispatch = useDispatch();

	const controlOwnerValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlOwnerValue,
	);
	const controlTypeValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlTypeValue,
	);
	const controlFrequencyValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlFrequencyValue,
	);
	const institutionValue = useSelector(
		(state: RootState) => state.simulationArena.simulationInstitutionValue,
	);

	const { REGULATION_SUMMARY, CONTROL_STATEMENT } = AI_SIMULATION_TYPES;
	const { OK, FORBIDDEN } = HTTP_STATUS_CODES;

	const handleGenerate = async () => {
		if (!inputTxt) return;
		setIsGenerating(true);
		let result;
		if (workflowStep === CONTROL_STATEMENT) {
			result = await (requestFn as IFourParamFn)(
				inputTxt,
				formatSelectValue(controlFrequencyValue),
				formatSelectValue(controlTypeValue),
				controlOwnerValue.trim(),
			);
		} else if (workflowStep === REGULATION_SUMMARY) {
			result = await (requestFn as IRegulationParamFn)(
				aiModelKey,
				inputTxt,
				institutionValue.trim(),
			);
		} else {
			result = await (requestFn as IGenerateParamFn)(aiModelKey, inputTxt);
		}
		if (result.statusCode === OK) {
			setOutputResult(result.responseBody.answer);
		} else if (result.statusCode === FORBIDDEN) {
			dispatch(
				setToastMessage({
					message: PERMISSION_DENIED_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsGenerating(false);
	};

	const handlePromptSelect = async (e: any) => {
		if (!inputTxt) return;
		const promptKey = e.key;
		setIsGenerating(true);
		let result;
		if (workflowStep === CONTROL_STATEMENT) {
			result = await (reqPromptSelect as IFourParamFn)(
				inputTxt,
				formatSelectValue(controlFrequencyValue),
				formatSelectValue(controlTypeValue),
				controlOwnerValue.trim(),
				promptKey,
			);
		} else if (workflowStep === REGULATION_SUMMARY) {
			result = await (reqPromptSelect as IRegulationParamFn)(
				promptKey,
				inputTxt,
				institutionValue.trim(),
			);
		} else {
			result = await (reqPromptSelect as IGenerateParamFn)(promptKey, inputTxt);
		}
		if (result.statusCode === OK) {
			setOutputResult(result.responseBody.answer);
		} else if (result.statusCode === FORBIDDEN) {
			dispatch(
				setToastMessage({
					message: PERMISSION_DENIED_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsGenerating(false);
	};

	return {
		inputTxt,
		updateInput: setInputTxt,
		updateOutput: setOutputResult,
		handleGenerate,
		handlePromptSelect,
		outputResult,
		isGenerating,
	};
}
